/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP Form Component
 * =============================================================================
 *
 * Reusable form component for creating and editing RFPs.
 * Handles validation, error display, and form submission.
 *
 * @module pages/rfps/RFPForm
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';

// =============================================================================
// CONSTANTS
// =============================================================================

const CATEGORIES = [
  { value: '', label: 'Select Category' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'civil', label: 'Civil' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'structural', label: 'Structural' },
  { value: 'geotechnical', label: 'Geotechnical' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'water_resources', label: 'Water Resources' },
  { value: 'other', label: 'Other' },
];

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'tracking', label: 'Tracking' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'no_bid', label: 'No Bid' },
  { value: 'cancelled', label: 'Cancelled' },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format date string for input[type="date"].
 */
function formatDateForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
}

/**
 * Format date string for input[type="datetime-local"].
 */
function formatDateTimeForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16);
}

// =============================================================================
// RFP FORM COMPONENT
// =============================================================================

/**
 * RFP Form component.
 *
 * @param {Object} props - Component props
 * @param {Object} [props.initialData] - Initial form data for editing
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isSubmitting - Loading state
 * @param {string} props.submitLabel - Submit button label
 */
function RFPForm({ initialData, onSubmit, onCancel, isSubmitting, submitLabel = 'Save' }) {
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    rfpNumber: '',
    description: '',
    companyName: '',
    category: '',
    subcategory: '',
    issueDate: '',
    questionsDueDate: '',
    submissionDeadline: '',
    expectedAwardDate: '',
    projectStartDate: '',
    estimatedBudgetMin: '',
    estimatedBudgetMax: '',
    budgetCurrency: 'USD',
    status: 'draft',
    source: '',
    sourceUrl: '',
    leadUserName: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        rfpNumber: initialData.rfpNumber || '',
        description: initialData.description || '',
        companyName: initialData.companyName || '',
        category: initialData.category || '',
        subcategory: initialData.subcategory || '',
        issueDate: formatDateForInput(initialData.issueDate),
        questionsDueDate: formatDateTimeForInput(initialData.questionsDueDate),
        submissionDeadline: formatDateTimeForInput(initialData.submissionDeadline),
        expectedAwardDate: formatDateForInput(initialData.expectedAwardDate),
        projectStartDate: formatDateForInput(initialData.projectStartDate),
        estimatedBudgetMin: initialData.estimatedBudgetMin || '',
        estimatedBudgetMax: initialData.estimatedBudgetMax || '',
        budgetCurrency: initialData.budgetCurrency || 'USD',
        status: initialData.status || 'draft',
        source: initialData.source || '',
        sourceUrl: initialData.sourceUrl || '',
        leadUserName: initialData.leadUserName || '',
      });
    }
  }, [initialData]);

  /**
   * Handle input change.
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  /**
   * Validate form data.
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.rfpNumber.trim()) {
      newErrors.rfpNumber = 'RFP number is required';
    }

    if (!formData.submissionDeadline) {
      newErrors.submissionDeadline = 'Submission deadline is required';
    }

    if (formData.sourceUrl && !isValidUrl(formData.sourceUrl)) {
      newErrors.sourceUrl = 'Please enter a valid URL';
    }

    if (formData.estimatedBudgetMin && formData.estimatedBudgetMax) {
      const min = parseFloat(formData.estimatedBudgetMin);
      const max = parseFloat(formData.estimatedBudgetMax);
      if (min > max) {
        newErrors.estimatedBudgetMin = 'Minimum cannot exceed maximum';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Check if URL is valid.
   */
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Handle form submission.
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Clean up data before submitting
    const submitData = { ...formData };

    // Convert budget to numbers before null conversion
    if (submitData.estimatedBudgetMin && submitData.estimatedBudgetMin !== '') {
      submitData.estimatedBudgetMin = parseFloat(submitData.estimatedBudgetMin);
    }
    if (submitData.estimatedBudgetMax && submitData.estimatedBudgetMax !== '') {
      submitData.estimatedBudgetMax = parseFloat(submitData.estimatedBudgetMax);
    }

    // Convert empty strings to null for optional fields
    Object.keys(submitData).forEach((key) => {
      if (submitData[key] === '') {
        submitData[key] = null;
      }
    });

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-accent-800">Basic Information</h2>
        </div>
        <div className="card-body space-y-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className="label">
              Title <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`input w-full ${errors.title ? 'border-error' : ''}`}
              placeholder="Enter RFP title"
            />
            {errors.title && <p className="mt-1 text-sm text-error">{errors.title}</p>}
          </div>

          {/* RFP Number */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="rfpNumber" className="label">
                RFP Number <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="rfpNumber"
                name="rfpNumber"
                value={formData.rfpNumber}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`input w-full ${errors.rfpNumber ? 'border-error' : ''}`}
                placeholder="e.g., RFP-2024-001"
              />
              {errors.rfpNumber && <p className="mt-1 text-sm text-error">{errors.rfpNumber}</p>}
            </div>

            <div>
              <label htmlFor="status" className="label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              >
                {STATUSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Company */}
          <div>
            <label htmlFor="companyName" className="label">
              Company / Client
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              disabled={isSubmitting}
              className="input w-full"
              placeholder="Issuing company name"
            />
          </div>

          {/* Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="label">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              >
                {CATEGORIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="subcategory" className="label">
                Subcategory
              </label>
              <input
                type="text"
                id="subcategory"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
                placeholder="Optional subcategory"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={isSubmitting}
              rows={4}
              className="input w-full"
              placeholder="RFP requirements and details..."
            />
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-accent-800">Timeline</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="issueDate" className="label">
                Issue Date
              </label>
              <input
                type="date"
                id="issueDate"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="questionsDueDate" className="label">
                Questions Due
              </label>
              <input
                type="datetime-local"
                id="questionsDueDate"
                name="questionsDueDate"
                value={formData.questionsDueDate}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="submissionDeadline" className="label">
                Submission Deadline <span className="text-error">*</span>
              </label>
              <input
                type="datetime-local"
                id="submissionDeadline"
                name="submissionDeadline"
                value={formData.submissionDeadline}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`input w-full ${errors.submissionDeadline ? 'border-error' : ''}`}
              />
              {errors.submissionDeadline && (
                <p className="mt-1 text-sm text-error">{errors.submissionDeadline}</p>
              )}
            </div>

            <div>
              <label htmlFor="expectedAwardDate" className="label">
                Expected Award Date
              </label>
              <input
                type="date"
                id="expectedAwardDate"
                name="expectedAwardDate"
                value={formData.expectedAwardDate}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="projectStartDate" className="label">
                Project Start Date
              </label>
              <input
                type="date"
                id="projectStartDate"
                name="projectStartDate"
                value={formData.projectStartDate}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Budget */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-accent-800">Budget</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="estimatedBudgetMin" className="label">
                Minimum Budget
              </label>
              <input
                type="number"
                id="estimatedBudgetMin"
                name="estimatedBudgetMin"
                value={formData.estimatedBudgetMin}
                onChange={handleChange}
                disabled={isSubmitting}
                min="0"
                step="0.01"
                className={`input w-full ${errors.estimatedBudgetMin ? 'border-error' : ''}`}
                placeholder="0.00"
              />
              {errors.estimatedBudgetMin && (
                <p className="mt-1 text-sm text-error">{errors.estimatedBudgetMin}</p>
              )}
            </div>

            <div>
              <label htmlFor="estimatedBudgetMax" className="label">
                Maximum Budget
              </label>
              <input
                type="number"
                id="estimatedBudgetMax"
                name="estimatedBudgetMax"
                value={formData.estimatedBudgetMax}
                onChange={handleChange}
                disabled={isSubmitting}
                min="0"
                step="0.01"
                className="input w-full"
                placeholder="0.00"
              />
            </div>

            <div>
              <label htmlFor="budgetCurrency" className="label">
                Currency
              </label>
              <select
                id="budgetCurrency"
                name="budgetCurrency"
                value={formData.budgetCurrency}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
              >
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-accent-800">Additional Information</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="source" className="label">
                Source
              </label>
              <input
                type="text"
                id="source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
                placeholder="e.g., Government portal, LinkedIn"
              />
            </div>

            <div>
              <label htmlFor="leadUserName" className="label">
                Lead / Owner
              </label>
              <input
                type="text"
                id="leadUserName"
                name="leadUserName"
                value={formData.leadUserName}
                onChange={handleChange}
                disabled={isSubmitting}
                className="input w-full"
                placeholder="Assigned team lead"
              />
            </div>
          </div>

          <div>
            <label htmlFor="sourceUrl" className="label">
              Source URL
            </label>
            <input
              type="url"
              id="sourceUrl"
              name="sourceUrl"
              value={formData.sourceUrl}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`input w-full ${errors.sourceUrl ? 'border-error' : ''}`}
              placeholder="https://..."
            />
            {errors.sourceUrl && <p className="mt-1 text-sm text-error">{errors.sourceUrl}</p>}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel} disabled={isSubmitting} className="btn-outline">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default RFPForm;
